import { useCallback, useEffect, useRef } from 'react';
import type { Strategy } from '../types/Strategy';
import { ApiService } from '../services/ApiService';
import { normalizeNodeMetadata } from '../utils/metadataUtils';
import type { StrategyResult, WorkspaceState } from './useWorkspaceState';
import { toast } from 'sonner';

interface UseMeepMeepJobsParams {
    strategies: Strategy[];
    setWorkspaceState: (patch: Partial<WorkspaceState>) => void;
    setStrategyResult: (id: string, result: Partial<StrategyResult>) => void;
}

export function useMeepMeepJobs({
    strategies,
    setWorkspaceState,
    setStrategyResult,
}: UseMeepMeepJobsParams) {
    const strategiesRef = useRef<Strategy[]>(strategies);
    const activeMeepMeepJobsRef = useRef<string[]>([]);
    const activeMeepMeepJobsKeyRef = useRef<string>('');
    const meepMeepPollingInFlightRef = useRef<Set<string>>(new Set());
    const meepMeepNotifiedJobsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        strategiesRef.current = strategies;
        activeMeepMeepJobsRef.current = strategies
            .filter((strategy) => strategy.loadingMode === 'meepmeep' && strategy.meepMeepJobId)
            .map((strategy) => strategy.meepMeepJobId)
            .filter((jobId): jobId is string => Boolean(jobId));
    }, [strategies]);

    const notifyMeepMeepTerminalState = useCallback((strategy: Strategy, status: string, message: string | null) => {
        const text = `MeepMeep job ${status} for "${strategy.name}"${message ? `: ${message}` : ''}`;
        
        if (status === 'completed') {
            toast.success(text);
        } else if (status === 'failed') {
            toast.error(text);
        } else {
            toast.info(text);
        }
    }, []);

    const applyMeepMeepTerminalState = useCallback((strategy: Strategy, payload: any) => {
        const status = payload.status;
        const result = payload.result;
        const error = payload.error;

        if (status === 'completed' && result) {
            const resultData = result.nature === 'meepmeep_result' ? result : (result.data ?? null);
            setStrategyResult(strategy.id, {
                result: resultData,
                error: result.error ?? null,
                nature: result.nature ?? 'meepmeep_result',
                display_type: result.display_type ?? undefined,
                backendStats: result.stats ?? null,
                chartData: result.chart_data ?? null,
                chartSeriesLabels: result.chart_series_labels ?? null,
                nodeMetadata: normalizeNodeMetadata(result.node_metadata, result, strategy.nodes[0]?.id),
                isLoading: false,
                loadingMode: null,
                meepMeepJobId: null,
                meepMeepJobStatus: 'completed',
                meepMeepJobMessage: payload.message ?? null,
                meepMeepJobStage: 'completed',
                meepMeepCurrentDayCount: payload.current_day_count ?? null,
                meepMeepTotalDayCount: payload.total_day_count ?? null,
                meepMeepProgressPct: payload.progress_pct ?? 100,
                meepMeepRequestTree: strategy.meepMeepRequestTree ?? null,
            });
            setWorkspaceState({ activeTab: 'results' });
            return;
        }

        const errorMessage = error?.error || payload.message || (status === 'cancelled' ? 'MeepMeep evaluation cancelled.' : 'MeepMeep evaluation failed.');
        setStrategyResult(strategy.id, {
            result: null,
            error: errorMessage,
            nature: 'meepmeep_result',
            backendStats: null,
            isLoading: false,
            loadingMode: null,
            meepMeepJobId: null,
            meepMeepJobStatus: status,
            meepMeepJobMessage: errorMessage,
            meepMeepJobStage: status,
            meepMeepCurrentDayCount: payload.current_day_count ?? null,
            meepMeepTotalDayCount: payload.total_day_count ?? null,
            meepMeepProgressPct: payload.progress_pct ?? null,
            meepMeepRequestTree: strategy.meepMeepRequestTree ?? null,
        });
        setWorkspaceState({ activeTab: 'results' });
    }, [setStrategyResult, setWorkspaceState]);

    const pollMeepMeepJobs = useCallback(async () => {
        const activeMeepMeepStrategies = strategiesRef.current.filter(
            (strategy) => strategy.loadingMode === 'meepmeep' && strategy.meepMeepJobId,
        );
        if (activeMeepMeepStrategies.length === 0) return;

        await Promise.all(
            activeMeepMeepStrategies.map(async (strategy) => {
                const jobId = strategy.meepMeepJobId;
                if (!jobId || meepMeepPollingInFlightRef.current.has(jobId)) return;

                meepMeepPollingInFlightRef.current.add(jobId);
                try {
                    const payload = await ApiService.getMeepMeepJob(jobId);
                    const isActive = payload.status === 'queued' || payload.status === 'running' || payload.status === 'cancelling';
                    setStrategyResult(strategy.id, {
                        meepMeepJobStatus: payload.status ?? null,
                        meepMeepJobMessage: payload.message ?? null,
                        meepMeepJobStage: payload.current_stage ?? null,
                        meepMeepCurrentDayCount: payload.current_day_count ?? null,
                        meepMeepTotalDayCount: payload.total_day_count ?? null,
                        meepMeepProgressPct: payload.progress_pct ?? null,
                        isLoading: isActive,
                        loadingMode: isActive ? 'meepmeep' : null,
                    });

                    if (payload.status === 'completed' || payload.status === 'failed' || payload.status === 'cancelled') {
                        applyMeepMeepTerminalState(strategy, payload);
                        if (!meepMeepNotifiedJobsRef.current.has(jobId)) {
                            meepMeepNotifiedJobsRef.current.add(jobId);
                            notifyMeepMeepTerminalState(strategy, payload.status ?? 'unknown', payload.message ?? null);
                        }
                    }
                } catch (err: unknown) {
                    const errorMessage = err instanceof Error ? err.message : 'Failed to poll MeepMeep job.';
                    setStrategyResult(strategy.id, {
                        result: null,
                        error: errorMessage,
                        nature: 'meepmeep_result',
                        backendStats: null,
                        isLoading: false,
                        loadingMode: null,
                        meepMeepJobId: null,
                        meepMeepJobStatus: 'failed',
                        meepMeepJobMessage: errorMessage,
                        meepMeepJobStage: 'failed',
                    });
                } finally {
                    meepMeepPollingInFlightRef.current.delete(jobId);
                }
            }),
        );
    }, [applyMeepMeepTerminalState, notifyMeepMeepTerminalState, setStrategyResult]);

    const cancelMeepMeepJob = useCallback(async (strategy: Strategy) => {
        const jobId = strategy.meepMeepJobId;
        if (!jobId) return;

        setStrategyResult(strategy.id, {
            meepMeepJobStatus: 'cancelling',
            meepMeepJobMessage: 'Cancelling MeepMeep evaluation...',
            meepMeepJobStage: 'cancelling',
            isLoading: true,
            loadingMode: 'meepmeep',
        });

        try {
            const json = await ApiService.cancelMeepMeepJob(jobId);
            const terminalStatus = json.status === 'cancelled' ? 'cancelled' : 'cancelling';

            setStrategyResult(strategy.id, {
                meepMeepJobStatus: terminalStatus,
                meepMeepJobMessage: json.message ?? (terminalStatus === 'cancelled' ? 'MeepMeep evaluation cancelled.' : 'Cancelling MeepMeep evaluation...'),
                meepMeepJobStage: json.current_stage ?? terminalStatus,
                meepMeepCurrentDayCount: json.current_day_count ?? strategy.meepMeepCurrentDayCount ?? null,
                meepMeepTotalDayCount: json.total_day_count ?? strategy.meepMeepTotalDayCount ?? null,
                meepMeepProgressPct: json.progress_pct ?? strategy.meepMeepProgressPct ?? null,
                isLoading: terminalStatus !== 'cancelled',
                loadingMode: terminalStatus !== 'cancelled' ? 'meepmeep' : null,
            });

            if (terminalStatus === 'cancelled') {
                setStrategyResult(strategy.id, {
                    result: null,
                    error: 'MeepMeep evaluation cancelled.',
                    nature: 'meepmeep_result',
                    backendStats: null,
                    isLoading: false,
                    loadingMode: null,
                    meepMeepJobId: null,
                });
            }
        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : 'Failed to cancel MeepMeep evaluation.';
            setStrategyResult(strategy.id, {
                meepMeepJobStatus: 'running',
                meepMeepJobMessage: errorMessage,
                isLoading: true,
                loadingMode: 'meepmeep',
            });
            toast.error(errorMessage);
        }
    }, [setStrategyResult]);

    return {
        pollMeepMeepJobs,
        activeMeepMeepJobsKeyRef,
        cancelMeepMeepJob,
    };
}
